import {sortByKey, uniqueObjects, unravel} from "./util";
import {getEntityConfigs} from "@/entityConfigs";
import {collectionFilterLabel} from "@/collectionFilter";
import countryCodeLookup from "country-code-lookup";
import {continentForCountryCode} from "@/continents";

// Alternate names are alternatives *to* the display name, so the display name
// itself should never appear in the list.
const altNames = (entity, field) =>
    (entity[field] || []).filter(name => name && name !== entity.display_name);

const facetCategories = {
    works: [
        "aboutness",
        "author",
        "source",
        "funder",
        "institution",
        "geo",
        "open access",
        "citation",
        "ids",
        "other",
    ],
    authors: [
        "institution",
        "geo",
        "citation",
        "ids",
        "other",
    ],
    sources: [
        "aboutness",
        "open access",
        "geo",
        "citation",
        "ids",
        "other",
    ],
    publishers: [
        "citation",
        "ids",
        "other",
    ],
    funders: [
        "geo",
        "citation",
        "ids",
        "other",
    ],
    institutions: [
        "geo",
        "citation",
        "ids",
        "other",
    ],
    concepts: [
        "citation",
        "ids",
        "other",
    ],
    locations: [
        "other",
    ],
    awards: [
        "aboutness",
        "funder",
        "investigator",
        "institution",
        "geo",
        "ids",
        "other",
    ],
    topics: [
        "citation",
        "ids",
        "other",
    ],
    subfields: [
        "citation",
        "other",
    ],
    fields: [
        "citation",
        "other",
    ],
    domains: [
        "citation",
        "other",
    ],
    types: [
        "citation",
        "other",
    ],
    continents: [
        "citation",
        "other",
    ],
    countries: [
        "citation",
        "other",
    ],
    languages: [
        "citation",
        "other",
    ],
    sdgs: [
        "citation",
        "other",
    ],
    "source-types": [
        "citation",
        "other",
    ],
    "institution-types": [
        "citation",
        "other",
    ],
    licenses: [
        "citation",
        "other",
    ],
    "oa-statuses": [
        "citation",
        "other",
    ],
    keywords: [
        "citation",
        "other",
    ],
}

const facetCategoriesIcons = {
    author: "mdi-account-outline",
    institution: "mdi-town-hall",
    geo: "mdi-map-marker-outline",
    funder: "mdi-cash-multiple",
    source: "mdi-book-multiple-outline",
    "open access": "mdi-lock-open-outline",
    ids: "mdi-tag-outline",
    citation: "mdi-format-quote-close",
    aboutness: "mdi-lightbulb-outline",
    investigator: "mdi-account-outline",
    other: "mdi-dots-horizontal",
}


const facetConfigs = function (entityType) {
    const ret = [
        // ============================================================
        // WORKS
        // ============================================================
        {
            key: "ids.openalex",
            entityToFilter: "works",
            entityToSelect: "works",
            displayName: "Work",
            isSingleWork: true,
            isId: true,
            type: "selectEntity",
            category: "ids",
            actions: ["column"],
            icon: "mdi-file-document-outline",
            extractFn: (entity) => entity.id,
            isMultiple: false,
            // As a COLUMN this is the work's own OpenAlex ID — label it "Work ID"
            // (the filter keeps "Work") and render/export the short form (W…),
            // not the canonical URL. `bare_openalex_id` strips the URL prefix
            // server-side so the CSV matches the table. See OWN_ID_COLUMN below.
            column: {
                label: "Work ID",
                render: { kind: "stringList", bareId: true },
                export: { recipe: "bare_openalex_id" },
            },
        },
        {
            key: "doi",
            entityToFilter: "works",
            entityToSelect: "works",
            displayName: "DOI",
            isSingleWork: true,
            isId: true,
            type: "selectEntity",
            category: "ids",
            actions: [],
            icon: "mdi-file-document-outline",
            extractFn: (entity) => entity.doi,
            isMultiple: false,
        },
        {
            key: "concepts.id",
            entityToFilter: "works",
            displayName: "concept",
            entityToSelect: "concepts",
            type: "selectEntity",
            isManyOptions: true,
            category: "aboutness",
            actions: [],
            actionsPopular: [],
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.concepts,
            isMultiple: true,
        },
        {
            key: "primary_topic.id",
            entityToFilter: "works",
            displayName: "topic",
            entityToSelect: "topics",
            type: "selectEntity",
            isManyOptions: true,
            category: "aboutness",
            actions: ["filter", "group_by", "edit"],
            actionsPopular: ["filter", "group_by"],
            icon: "mdi-tag-outline",
            isMultiple: false,
            extractFn: (entity) => entity.primary_topic,
        },
        {
            key: "keywords.id",
            entityToFilter: "works",
            displayName: "keyword",
            entityToSelect: "keywords",
            type: "selectEntity",
            isManyOptions: true,
            category: "aboutness",
            actions: ["filter", "group_by", "edit"],
            actionsPopular: [],
            icon: "mdi-tag-outline",
            isMultiple: false,
            extractFn: (entity) => entity.keywords,
        },
        {
            key: "primary_topic.subfield.id",
            entityToFilter: "works",
            displayName: "subfield",
            entityToSelect: "subfields",
            type: "selectEntity",
            isManyOptions: true,
            category: "aboutness",
            actions: ["filter", "group_by",],
            actionsPopular: [],
            icon: "mdi-tag-outline",
            isMultiple: false,
            extractFn: (entity) => entity.primary_topic?.subfield,
        },
        {
            key: "primary_topic.field.id",
            entityToFilter: "works",
            displayName: "field",
            entityToSelect: "fields",
            type: "selectEntity",
            isManyOptions: true,
            category: "aboutness",
            actions: ["filter", "group_by",],
            actionsPopular: [],
            icon: "mdi-tag-outline",
            isMultiple: false,
            extractFn: (entity) => entity.primary_topic?.field,
        },
        {
            key: "primary_topic.domain.id",
            entityToFilter: "works",
            displayName: "domain",
            entityToSelect: "domains",
            type: "selectEntity",
            isManyOptions: true,
            category: "aboutness",
            actions: ["filter", "group_by",],
            actionsPopular: [],
            icon: "mdi-tag-outline",
            isMultiple: false,
            extractFn: (entity) => entity.primary_topic?.domain,
        },
        {
            key: "awards.id",
            entityToFilter: "works",
            entityToSelect: "awards",
            displayName: "awards",
            type: "selectEntity",
            isManyOptions: true,
            category: "funder",
            actions: ["filter", "edit"],
            actionsPopular: [],
            icon: "mdi-cash-multiple",
            extractFn: (entity) => {
                if (!entity.awards) return [];
                return entity.awards.map(award => {
                    if (!award) return null;
                    // Extract short ID (e.g., "G5453342221" from "https://openalex.org/G5453342221")
                    const shortId = award.id?.split('/').pop() || award.id;
                    return {
                        id: award.id,
                        display_name: award.title || award.display_name || shortId
                    };
                }).filter(a => a !== null);
            },
            isMultiple: true,
        },
        {
            key: "funders.id",
            entityToFilter: "works",
            entityToSelect: "funders",
            displayName: "funder",
            type: "selectEntity",
            isManyOptions: true,
            category: "funder",
            actions: ["filter", "group_by"],
            actionsPopular: ["filter", "group_by"],
            icon: "mdi-cash-multiple",
            semanticSearchAllowed: true,
            // The client extractFn dedupes funders; the server's mechanical
            // flatten doesn't. Apply the `unique` recipe on both name + :ids
            // variants to match. Path auto-derives ("funders.display_name").
            column: { export: { recipe: "unique" } },
            extractFn: (entity) => {
                const funders = entity.funders || [];
                return uniqueObjects(funders.filter(funder => funder?.id));
            },
            isMultiple: true,
        },
        {
            key: "authorships.institutions.lineage",
            entityToFilter: "works",
            displayName: "institution",
            entityToSelect: "institutions",
            type: "selectEntity",
            isManyOptions: true,
            category: "institution",
            actions: ["filter", "group_by",],
            actionsPopular: ["filter", "group_by",],
            icon: "mdi-town-hall",
            semanticSearchAllowed: true,
            // The key is `lineage` (a filter affordance — accepts an
            // institution's ancestor IDs), but the COLUMN renders the
            // deduped per-author institutions. Server has no `lineage`
            // flat-path matching this concept; map to display_name + dedupe.
            // The :ids variant auto-derives to `authorships.institutions.id`.
            column: {
                export: {
                    path: "authorships.institutions.display_name",
                    recipe: "unique",
                },
            },
            extractFn: (entity) => {
                const nested = entity.authorships.map(authorship => {
                    return authorship.institutions
                })
                // Filter out institutions with null id (fully null objects from API)
                const filtered = nested.flat().filter(inst => inst && inst.id)
                return uniqueObjects(filtered)
            },
            isMultiple: true,
        },
        {
            key: "authorships.institutions.ror",
            entityToFilter: "works",
            entityToSelect: "institutions",
            displayName: "ROR ID",
            isId: true,
            type: "selectEntity",
            category: "ids",
            actions: ["column"],
            icon: "mdi-town-hall",
            // extractFn returns an array of full ROR URLs -> stringList auto-linkifies each.
            column: { render: { kind: "stringList" } },
            extractFn: (entity) => {
                const nested = entity.authorships.map(authorship => {
                    // Filter out institutions with null id before accessing ror property
                    return authorship.institutions.filter(insti => insti && insti.id).map(insti => insti.ror)
                })
                return nested.flat()
            },
            isMultiple: true,
        },
        {
            key: "authorships.author.id",
            entityToFilter: "works",
            displayName: "author",
            entityToSelect: "authors",
            type: "selectEntity",
            isManyOptions: true,
            category: "author",
            actions: ["filter", "group_by", "edit", "column"],
            actionsPopular: ["filter", "column",],
            icon: "mdi-account-outline",
            semanticSearchAllowed: true,
            // entityList of author objects. itemLabelField/itemLinkField name the
            // snake_case API fields on each extracted item (display_name / id) —
            // NOT the camelCase `displayName` property label above.
            column: { render: { kind: "entityList", itemLabelField: "display_name", itemLinkField: "id" } },
            extractFn: (entity) => {
                return entity.authorships.map(authorship => {
                    // If we have a full author object, return it with raw_author_name attached
                    if (authorship.author) {
                        return {
                            ...authorship.author,
                            // Include raw_author_name for display on entity pages
                            raw_author_name: authorship.raw_author_name
                        };
                    }
                    // If we only have raw_author_name, create a pseudo-object
                    // This won't have an ID (so no link), but will have a display_name
                    if (authorship.raw_author_name) {
                        return {
                            display_name: authorship.raw_author_name,
                            raw_author_name: authorship.raw_author_name,
                            id: null // No ID means EntityDatumRow won't create a link
                        };
                    }
                    // No author info at all
                    return null;
                }).filter(author => author !== null); // Remove nulls
            },
            isMultiple: true,
        },
        {
            key: "authorships.author.orcid",
            entityToFilter: "works",
            entityToSelect: "authors",
            displayName: "ORCID",
            isId: true,
            type: "selectEntity",
            category: "ids",
            actions: ["edit"],
            icon: "mdi-account-outline",
            extractFn: (entity) => {
                return entity.authorships.map(authorship => {
                    return authorship.author.orcid
                })
            },
            isMultiple: true,
        },
        // works: search
        {
            key: "default.search",
            entityToFilter: "works",
            // Searches title + abstract + full-text body (NOT author/affiliation).
            // Labeled "full text" per the Europe PMC convention, where "full text"
            // means the complete article text. Was wrongly labeled "fulltext"
            // (which read as body-only); see oxjob #374 / zd#8310.
            displayName: "full text",
            type: "search",
            actions: ["filter",],
            actionsPopular: [],
            category: "other",
            icon: "mdi-magnify",
            isMultiple: false,
        },
        {
            // Full-text BODY only (excludes title/abstract) — the unusual scope no
            // peer system surfaces as a default. Qualified as "body text" to
            // distinguish it from default.search's "full text". oxjob #374.
            key: "fulltext.search",
            entityToFilter: "works",
            displayName: "body text",
            type: "search",
            actions: ["filter",],
            actionsPopular: [],
            category: "other",
            icon: "mdi-magnify",
            isMultiple: false,
        },
        {
            key: "title_and_abstract.search",
            entityToFilter: "works",
            // "title/abstract" (slash, PubMed [tiab] convention) keeps the two
            // words cohering as one scope and avoids the false-conjunction read
            // of "title and abstract". oxjob #374.
            displayName: "title/abstract",
            type: "search",
            actions: ["filter",],
            actionsPopular: ["filter"],
            category: "other",
            icon: "mdi-magnify",
            isMultiple: false,
        },
        {
            key: "display_name.search",
            entityToFilter: "works",
            displayName: "title",
            actions: ["filter",],
            actionsPopular: [],
            type: "search",
            category: "other",
            icon: "mdi-magnify",
        },
        {
            // Alias for display_name.search to support URLs like
            // /works?filter=title.search:ai
            key: "title.search",
            entityToFilter: "works",
            displayName: "title",
            actions: ["filter",],
            actionsPopular: [],
            type: "search",
            category: "other",
            icon: "mdi-magnify",
        },
        {
            key: "raw_affiliation_strings.search",
            entityToFilter: "works",
            displayName: "raw affiliation",
            type: "search",
            actions: ["filter",],
            actionsPopular: ["",],
            category: "other",
            icon: "mdi-magnify",
            isMultiple: false,
        },
        {
            key: "raw_affiliation_strings",
            entityToFilter: "works",
            displayName: "raw affiliation (exact)",
            type: "search",
            actions: ["filter",],
            actionsPopular: [],
            category: "other",
            icon: "mdi-map-marker-outline",
            isMultiple: false,
            verb: "is exactly",
        },
        {
            key: "doi_starts_with",
            entityToFilter: "works",
            displayName: "DOI prefix",
            type: "search",
            actions: ["filter",],
            actionsPopular: [],
            category: "other",
            icon: "mdi-magnify",
            isMultiple: false,
            verb: "starts with",
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "works",
            displayName: "title",
            type: "search",
            actions: ["sort", "column", "edit"],
            actionsPopular: ["sort", "column",],
            category: "other",
            icon: "mdi-file-document-outline",
            // The mandatory identity column. ResultsTable renders it as a link
            // to the row's own entity (passes the whole row object), so the
            // entityLink kind reads display_name + id off the work itself.
            column: { render: { kind: "entityLink" } },
            extractFn: (entity) => entity.display_name,
            isMultiple: false,
        },
        {
            key: "has_abstract",
            entityToFilter: "works",
            displayName: "Abstract available",
            type: "boolean",
            actions: ["filter"],
            category: "other",
            icon: "mdi-file-document-outline",
            semanticSearchAllowed: true,
        },

        // works: authors
        {
            key: "authors_count",
            entityToFilter: "works",
            displayName: "authors count",
            type: "range",
            sortByValue: true,
            category: "author",
            actions: ["filter", "sort", "column",],
            icon: "mdi-account-outline",
            extractFn: (entity) => entity.authors_count,
            isMultiple: false,
        },
        {
            key: "corresponding_author_ids",
            entityToFilter: "works",
            entityToSelect: "authors",
            displayName: "corresponding author",
            type: "selectEntity",
            isManyOptions: true,
            category: "author",
            actions: ["filter", "group_by", "edit"],
            icon: "mdi-email-outline",
            isMultiple: true,
        },
        // works: open access
        {
            key: "open_access.is_oa",
            entityToFilter: "works",
            displayName: "open access",
            type: "boolean",
            booleanValues: ["NOT Open Access", "Open Access"],
            actions: ["filter", "column", "group_by",],
            actionsPopular: ["filter", "column", "group_by",],
            category: "open access",
            icon: "mdi-lock-open-outline",
            isMultiple: false,
            semanticSearchAllowed: true,
            // CellValue reads booleanValues above for the rendered labels.
            column: { render: { kind: "boolean" } },
            extractFn: (entity) => entity.open_access?.is_oa,
        },
        {
            key: "has_content.pdf",
            entityToFilter: "works",
            displayName: "linked to a PDF",
            type: "boolean",
            booleanValues: ["not linked to a PDF", "linked to a PDF"],
            actions: ["filter", "column", "group_by",],
            actionsPopular: [],
            category: "open access",
            icon: "mdi-file-pdf-box",
            isMultiple: false,
        },
        {
            key: "best_oa_location.license",
            entityToFilter: "works",
            entityToSelect: "licenses",
            displayName: "license",
            type: "selectEntity",
            // actions: [],
            actions: ["filter", "column", "group_by"],
            category: "open access",
            icon: "mdi-lock-open-outline",
            displayNullAs: "All rights reserved",
            isMultiple: false,
        },
        {   key: "locations.license", // Added as stub to prevent JS errors
            entityToFilter: "works",
            entityToSelect: "licenses",
            displayName: "license",
            type: "selectEntity",
            actions: ["edit"],
            category: "open access",
            icon: "mdi-lock-open-outline",
            displayNullAs: "All rights reserved",
            isMultiple: false,
        },
        {
            // works but with workarounds because entity endpoints don't exist
            key: "open_access.oa_status",
            entityToSelect: "oa-statuses",
            entityToFilter: "works",
            displayName: "Open Access status",
            type: "selectEntity",
            actions: ["filter", "column", "group_by","edit"],
            category: "open access",
            icon: "mdi-lock-open-outline",
            isMultiple: false,
            extractFn: (entity) => entity.open_access.oa_status,
            column: { export: { path: "open_access.oa_status" } },
        },
        {
            key: "best_oa_location.is_accepted",
            entityToFilter: "works",
            displayName: "open access accepted",
            type: "boolean",
            booleanValues: ["NOT Open Access", "Open Access"],
            actions: ["filter", "column", "group_by",],
            category: "open access",
            icon: "mdi-lock-open-outline",
            isMultiple: false,
        },
        {
            key: "best_oa_location.is_published",
            entityToFilter: "works",
            displayName: "open access published",
            type: "boolean",
            booleanValues: ["NOT Open Access", "Open Access"],
            actions: ["filter", "column", "group_by",],
            category: "open access",
            icon: "mdi-lock-open-outline",
            isMultiple: false,
        },
        // works: APC
        {
            key: "apc_paid.value_usd",
            entityToFilter: "works",
            displayName: "APC paid (est)",
            type: "range",
            sortByValue: true,
            category: "other",
            actions: ["filter","edit","column"],
            icon: "mdi-cash",
            column: { render: { kind: "currency", currency: "USD" } },
            extractFn: (entity) => entity.apc_paid?.value_usd,
        },

        // works: primary source
        {
            key: "primary_location.source.id",
            entityToFilter: "works",
            displayName: "source",
            entityToSelect: "sources",
            type: "selectEntity",
            isManyOptions: true,
            category: "source",
            actions: ["filter", "group_by",],
            icon: "mdi-book-open-outline",
            semanticSearchAllowed: true,
            extractFn: (entity) => entity.primary_location.source,
            isMultiple: false,
        },

        // works: source across all locations (primary + alternate)
        {
            key: "locations.source.id",
            entityToFilter: "works",
            displayName: "source (any location)",
            entityToSelect: "sources",
            type: "selectEntity",
            isManyOptions: true,
            category: "source",
            actions: ["filter"],
            icon: "mdi-book-open-outline",
            // The column name says "any location" — extract the source from
            // every location, not just primary. Filter out locations whose
            // .source is null (e.g. webpages).
            extractFn: (entity) => (entity.locations ?? [])
                .map((loc) => loc.source)
                .filter((s) => s),
            isMultiple: false,
        },
        {
            key: "primary_location.source.issn",
            entityToFilter: "works",
            entityToSelect: "sources",
            displayName: "ISSN",
            isId: true,
            type: "selectEntity",
            category: "ids",
            actions: [],
            icon: "mdi-book-open-outline",
            isMultiple: false,
        },
        {
            key: "primary_location.source.type",
            entityToFilter: "works",
            entityToSelect: "sources",
            displayName: "source type",
            type: "selectEntity",
            category: "source",
            actions: ["filter", "column", "group_by",],
            icon: "mdi-book-open-outline",
            isMultiple: false,
        },
        {
            key: "primary_location.source.is_in_doaj",
            entityToFilter: "works",
            displayName: "indexed by DOAJ",
            type: "boolean",
            booleanValues: ["Not in DOAJ", "In DOAJ"],
            category: "source",
            actions: ["filter", "group_by",],
            icon: "mdi-book-open-outline",
            isMultiple: false,
        },
        {
            key: "primary_location.source.is_core",
            entityToFilter: "works",
            displayName: "CWTS Core source",
            type: "boolean",
            booleanValues: ["Not CWTS Core source", "CWTS Core source"],
            category: "source",
            actions: ["filter", "group_by",],
            icon: "mdi-book-open-outline",
            isMultiple: false,
        },
        {
            key: "primary_location.source.is_oa",
            entityToFilter: "works",
            displayName: "in OA source",
            type: "boolean",
            booleanValues: ["Not Open Access", "Open Access"],
            category: "source",
            actions: ["filter", "column", "group_by",],
            icon: "mdi-book-open-outline",
            isMultiple: false,
        },
        {
            key: "primary_location.source.publisher_lineage",
            entityToFilter: "works",
            entityToSelect: "publishers",
            displayName: "publisher",
            type: "selectEntity",
            isManyOptions: true,
            category: "source",
            actions: ["filter", "group_by",],
            icon: "mdi-domain",
            isMultiple: false,
        },

        // works: institutions
        {
            key: "authorships.countries",
            entityToFilter: "works",
            entityToSelect: "countries",
            displayName: "Country",
            type: "selectEntity",
            isManyOptions: true,
            isCountry: true,
            actions: ["filter", "column", "group_by","edit"],
            actionsPopular: [],
            category: "geo",
            icon: "mdi-earth",
            isMultiple: true,
            // Column/export support. Without an extractFn this selectEntity
            // derives an entityList render kind but no usable extractFn, so
            // isColumnEligible() drops it — which is why "Country" was silently
            // absent from the column picker + CSV export (zd#8973). Render the
            // deduped country names in the table (countryCodeLookup, like
            // last_known_institutions.country_code); the CSV ships the raw ISO
            // codes via the authorships.countries flat path — the accepted
            // CODE_VS_NAME divergence (see parity.sweep.test.js).
            column: { render: { kind: "stringList" }, export: { path: "authorships.countries" } },
            extractFn: (entity) => {
                if (!Array.isArray(entity.authorships)) return [];
                const seen = new Set();
                const names = [];
                for (const authorship of entity.authorships) {
                    for (const code of (authorship.countries || [])) {
                        if (!code || seen.has(code)) continue;
                        seen.add(code);
                        const result = countryCodeLookup.byIso(String(code).toUpperCase());
                        names.push(result?.country || code);
                    }
                }
                return names;
            },
        },
        {
            key: "countries_distinct_count",
            entityToFilter: "works",
            displayName: "countries count",
            type: "range",
            sortByValue: true,
            actions: ["filter", "sort", "column",],
            category: "geo",
            icon: "mdi-earth",
            isMultiple: false,
        },
        {
            key: "institutions_distinct_count",
            entityToFilter: "works",
            displayName: "institutions count",
            type: "range",
            sortByValue: true,
            actions: ["filter", "sort", "column",],
            category: "institution",
            icon: "mdi-earth",
            isMultiple: false,
        },
        {
            key: "authorships.institutions.continent",
            entityToFilter: "works",
            entityToSelect: "continents",
            displayName: "Continent",
            type: "selectEntity",
            actions: ["filter", "column", "group_by",],
            actionsPopular: [],
            category: "geo",
            icon: "mdi-earth",
            // Column/export support. Like authorships.countries (zd#8973), this
            // had "column" in actions but no extractFn, so isColumnEligible()
            // dropped it from the picker. Unlike countries, the work JSON has NO
            // continent field — institutions carry only country_code — so both
            // the table and the CSV DERIVE continent from country codes. The
            // CSV exports the existing country_code flat path through the
            // server-side `country_to_continent` recipe, which mirrors the same
            // @/continents map (see openalex-users-api/formats/csv_manifest.py).
            column: {
                render: { kind: "stringList" },
                export: { path: "authorships.institutions.country_code", recipe: "country_to_continent" },
            },
            extractFn: (entity) => {
                if (!Array.isArray(entity.authorships)) return [];
                const seen = new Set();
                const continents = [];
                for (const authorship of entity.authorships) {
                    for (const institution of (authorship.institutions || [])) {
                        const continent = continentForCountryCode(institution.country_code);
                        if (!continent || seen.has(continent)) continue;
                        seen.add(continent);
                        continents.push(continent);
                    }
                }
                return continents;
            },
        },
        {
            key: "institutions.is_global_south",
            entityToFilter: "works",
            displayName: "from Global South",
            type: "boolean",
            actions: ["filter", "column", "group_by",],
            category: "geo",
            booleanValues: ["Global North", "Global South"],
            icon: "mdi-earth",
            // icon: "mdi-town-hall",
            isMultiple: true,
        },
        {
            key: "authorships.institutions.type",
            entityToFilter: "works",
            entityToSelect: "institution-types",
            displayName: "institution type",
            category: "institution",
            type: "selectEntity",
            actions: ["filter", "group_by",],
            icon: "mdi-town-hall",
            isMultiple: true,
        },
        {
            key: "corresponding_institution_ids",
            entityToFilter: "works",
            entityToSelect: "institutions",
            displayName: "corresponding institution",
            category: "institution",
            type: "selectEntity",
            isManyOptions: true,
            actions: ["filter", "group_by",],
            icon: "mdi-email-outline",
            isMultiple: true,
        },

        // works: repository
        {
            key: "open_access.any_repository_has_fulltext",
            entityToFilter: "works",
            displayName: "has repository fulltext",
            type: "boolean",
            booleanValues: ["Not in any repository", "In a repository"],
            category: "source",
            actions: ["filter", "column", "group_by",],
            icon: "mdi-tag-outline",
            isMultiple: true,
        },

        // works: intrinsic
        {
            key: "type",
            entityToFilter: "works",
            entityToSelect: "types",
            displayName: "type",
            type: "selectEntity",
            category: "other",
            actions: ["filter", "column", "group_by","edit"],
            actionsPopular: ["filter", "column", "group_by",],
            icon: "mdi-shape-outline",
            extractFn: (entity) => entity.type,
            isMultiple: false,
            semanticSearchAllowed: true,
            column: { export: { path: "type" } },
        },
        {
            key: "abstract",
            entityToFilter: "works",
            displayName: "Abstract",
            type: "search",
            category: "other",
            actions: ["edit", "column"],
            icon: "mdi-text",
            isMultiple: false,
            // Opt this search-type property in as a text column (the explicit
            // block overrides the type-based derivation, which skips `search`).
            // extractFn reconstructs the abstract from the inverted index.
            column: { render: { kind: "text" } },
            extractFn: (entity) => {
                if (!entity?.open_access?.is_oa) return
                return unravel(entity.abstract_inverted_index)
            },
        },
        {
            key: "publication_year",
            entityToFilter: "works",
            displayName: "year",
            isDate: true,
            type: "range",
            sortByValue: true,
            category: "other",
            actions: ["filter", "sort", "column", "group_by",],
            actionsPopular: ["filter", "sort", "column", "group_by",],
            icon: "mdi-calendar-range",
            column: { render: { kind: "year" } },
            extractFn: (entity) => entity.publication_year,
            isMultiple: false,
            semanticSearchAllowed: true,
        },
        {
            key: "from_created_date",
            entityToFilter: "works",
            displayName: "Created since date",
            isDate: true,
            type: "search",
            requiresApiKey: true,
            category: "other",
            actions: ["filter", "sort", "column"],
            actionsPopular: ["filter", "sort", "column",],
            icon: "mdi-calendar-range",
            extractFn: (entity) => entity.created_date,
            isMultiple: false,
        },
        {
            key: "from_updated_date",
            entityToFilter: "works",
            displayName: "Updated since date",
            isDate: true,
            type: "search",
            requiresApiKey: true,
            category: "other",
            actions: ["filter", "sort", "column"],
            actionsPopular: ["filter", "sort", "column",],
            icon: "mdi-calendar-range",
            extractFn: (entity) => entity.updated_date,
            isMultiple: false,
        },


        {
            key: "apc_sum",
            entityToFilter: "works",
            displayName: "APC sum",
            type: "sum",
            category: "other",
            actions: ["group_by",],
            actionsPopular: [],
            icon: "mdi-cash",
            isMultiple: false,
        },
        {
            key: "cited_by_count_sum",
            entityToFilter: "works",
            displayName: "Citations sum",
            type: "sum",
            category: "other",
            actions: ["group_by",],
            actionsPopular: [],
            icon: "mdi-format-quote-close",
            isMultiple: false,
        },
        {
            key: "publication_date",
            entityToFilter: "works",
            displayName: "date",
            isDate: true,
            type: "range",
            actions: ["sort",],
            sortByValue: true,
            category: "other",
            icon: "mdi-calendar-range",
            isMultiple: false,
        },
        {
            key: "has_doi",
            entityToFilter: "works",
            displayName: "has a DOI",
            type: "boolean",
            booleanValues: ["Has a DOI", "No DOI"],
            category: "ids",
            actions: ["filter", "group_by",],
            icon: "mdi-tag-outline",
            isMultiple: false,
        },
        {
            key: "indexed_in",
            entityToFilter: "works",
            displayName: "indexed in",
            type: "selectEntity",
            isStringOnly: true,
            category: "ids",
            actions: ["filter", "group_by",],
            icon: "mdi-tag-outline",
            isMultiple: true,
        },
        {
            key: "mag_only",
            entityToFilter: "works",
            displayName: "indexed by MAG only",
            type: "boolean",
            booleanValues: ["indexed by MAG only", "indexed beyond MAG"],
            category: "ids",
            actions: ["filter", "group_by",],
            icon: "mdi-tag-outline",
            isMultiple: false,
        },
        {
            key: "is_xpac",
            entityToFilter: "works",
            displayName: "New in Data Version 2 (xpac)",
            type: "boolean",
            booleanValues: ["In old data", "New in Data Version 2 (xpac)"],
            category: "other",
            actions: ["filter", "group_by",],
            actionsPopular: ["filter"],
            icon: "mdi-new-box",
            isMultiple: false,
        },
        {
            key: "has_orcid",
            entityToFilter: "works",
            displayName: "indexed by ORCID",
            type: "boolean",
            booleanValues: ["No ORCID", "At least one ORCID",],
            category: "ids",
            actions: ["filter", "group_by",],
            icon: "mdi-tag-outline",
            isMultiple: true,
        },
        {
            key: "has_pmid",
            entityToFilter: "works",
            displayName: "indexed by PubMed",
            type: "boolean",
            category: "ids",
            actions: ["filter", "group_by",],
            icon: "mdi-tag-outline",
            booleanValues: ["No PubMed ID", "Has PubMed ID"],
            isMultiple: false,
        },
        {
            key: "is_retracted",
            entityToFilter: "works",
            displayName: "retracted",
            type: "boolean",
            booleanValues: ["Isn't retracted", "Is retracted"],
            category: "other",
            actions: ["filter", "column", "group_by","edit"],
            icon: "mdi-close-octagon",
            isMultiple: false,
            semanticSearchAllowed: true,
        },
        {
            key: "language",
            entityToSelect: "languages",
            entityToFilter: "works",
            displayName: "language",
            type: "selectEntity",
            displayNullAs: "Unknown",
            category: "geo",
            actions: ["filter", "column", "group_by","edit"],
            actionsPopular: ["column"],
            icon: "mdi-translate",
            extractFn: (entity) => entity.language,
            isMultiple: false,
            column: { export: { path: "language" } },
        },
        {
            key: "sustainable_development_goals.id",
            entityToSelect: "sdgs",
            entityToFilter: "works",
            displayName: "Sustainable Development Goal",
            type: "selectEntity",
            displayNullAs: "Unknown",
            category: "aboutness",
            actions: ["filter", "group_by","edit"],
            icon: "mdi-sprout-outline",
            isMultiple: true,
            extractFn: (entity) => entity.sustainable_development_goals
        },
        {
            key: "cited_by_count",
            entityToFilter: "works",
            displayName: "citation count",
            type: "range",
            sortByValue: true,
            category: "citation",
            actions: ["filter", "sort", "column",],
            actionsPopular: ["sort", "column",],
            icon: "mdi-format-quote-close",
            column: { render: { kind: "number" } },
            extractFn: (entity) => entity.cited_by_count,
            isMultiple: false,
        },
        {
            key: "referenced_works_count",
            entityToFilter: "works",
            displayName: "references count",
            type: "range",
            sortByValue: true,
            category: "citation",
            actions: ["filter", "column",],
            actionsPopular: [],
            icon: "mdi-format-quote-close",
            isMultiple: false,
        },
        {
            key: "cited_by_percentile_year.min",
            entityToFilter: "works",
            displayName: "Citation percentile (year)",
            type: "range",
            sortByValue: true,
            category: "citation",
            actions: ["filter", "sort", "column",],
            actionsPopular: ["sort", "column",],
            icon: "mdi-format-quote-close",
            isMultiple: false,
            extractFn: (entity) => entity.cited_by_percentile_year.min,
        },
        {
            key: "citation_normalized_percentile.value",
            entityToFilter: "works",
            displayName: "Citation percentile (by year/subfield)",
            type: "range",
            sortByValue: true,
            category: "citation",
            actions: ["filter", "sort", "column",],
            actionsPopular: ["sort", "column",],
            icon: "mdi-format-quote-close",
            isMultiple: false,
            extractFn: (entity) => 100 * (entity.citation_normalized_percentile?.value || 0),
        },
        {
            key: "fwci",
            entityToFilter: "works",
            displayName: "FWCI",
            type: "range",
            sortByValue: true,
            category: "citation",
            actions: ["filter", "sort", "column",],
            actionsPopular: ["sort", "column",],
            icon: "mdi-format-quote-close",
            isMultiple: false,
            extractFn: (entity) => entity.fwci,
        },
        {
            key: "cited_by",
            entityToFilter: "works",
            entityToSelect: "works",
            displayName: "cited by",
            type: "selectEntity", // used to be "entity"
            category: "citation",
            actions: [],
            icon: "mdi-format-quote-close",
            isMultiple: true,
            isDisplayedAsCount: true,
            extractFn: (entity) => entity.cited_by_count,
            column: { export: { path: "cited_by_count" } },
        },
        {
            key: "cites",
            entityToFilter: "works",
            entityToSelect: "works",
            displayName: "cites",
            type: "selectEntity", // used to be "entity"
            category: "citation",
            actions: [],
            icon: "mdi-format-quote-close",
            isMultiple: true,
            isDisplayedAsCount: true,
            extractFn: (entity) => entity.referenced_works?.length,
            column: { export: { path: "referenced_works_count" } },
        },
        {
            key: "related_to",
            entityToFilter: "works",
            entityToSelect: "works",
            displayName: "related to",
            type: "selectEntity", // used to be "entity"
            category: "citation",
            actions: ["filter",],
            isHidden: true,
            icon: "mdi-book-open-outline",
            isMultiple: true,
            isDisplayedAsCount: true,
            extractFn: (entity) => entity.related_works?.length,
            // No `related_works_count` server field; the table shows the count
            // on screen, the CSV gets the underlying ID list (richer for
            // downstream analysis — matches Jason's "raw values" call on
            // numbers / currency).
            column: { export: { path: "related_works", recipe: "bare_openalex_id" } },
        },

        // ============================================================
        // AUTHORS
        // ============================================================
        {
            key: "ids.openalex",
            entityToFilter: "authors",
            entityToSelect: "authors",
            displayName: "author",
            type: "selectEntity",
            actions: ["column"],
            actionsPopular: [],
            isId: true,
            category: "ids",
            icon: "mdi-account-outline",
            // OWN_ID_COLUMN: the author's own OpenAlex ID, rendered + exported
            // as the short form (A…). See works ids.openalex.
            extractFn: (entity) => entity.id,
            column: {
                label: "Author ID",
                render: { kind: "stringList", bareId: true },
                export: { recipe: "bare_openalex_id" },
            },
        },
        {
            key: "ids.orcid",
            entityToFilter: "authors",
            entityToSelect: "authors",
            displayName: "ORCID",
            isId: true,
            type: "selectEntity",
            category: "ids",
            actions: ["edit"],
            icon: "mdi-account-outline",
            extractFn: (e) => e.ids.orcid
        },
        {
            key: "default.search",
            entityToFilter: "authors",
            entityToSelect: "authors",
            displayName: "name",
            type: "search",
            category: "other",
            actions: ["filter"],
            actionsPopular: ["filter"],
            icon: "mdi-magnify",
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "authors",
            displayName: "name",
            type: "search",
            actions: ["sort", "column", "edit"],
            actionsPopular: ["sort", "column",],
            category: "other",
            icon: "mdi-account-outline",
            extractFn: (entity) => entity.display_name,
            isMultiple: false,
        },
        {
            key: "affiliations.institution.id",
            entityToFilter: "authors",
            displayName: "Past institutions",
            entityToSelect: "institutions",
            type: "selectEntity",
            isManyOptions: true,
            category: "institution",
            actions: ["filter", "group_by"],
            actionsPopular: ["filter"],
            icon: "mdi-town-hall",
            extractFn: (entity) => {
                return entity.affiliations.map(affil => {
                    return affil.institution
                })
            },
        },
        {
            key: "affiliations.institution.type",
            entityToFilter: "authors",
            entityToSelect: "institution-types",
            displayName: "Past institutions type",
            type: "selectEntity",
            category: "institution",
            actions: ["filter", "group_by"],
            actionsPopular: ["filter",],
            icon: "mdi-shape-outline",
        },
        {
            key: "last_known_institutions.id",
            entityToFilter: "authors",
            displayName: "institution",
            entityToSelect: "institutions",
            type: "selectEntity",
            isManyOptions: true,
            category: "institution",
            actions: ["filter", "group_by"],
            actionsPopular: ["filter", "group_by"],
            icon: "mdi-town-hall",
            extractFn: (entity) => entity.last_known_institutions,
        },
        {
            key: "last_known_institutions.country_code",
            entityToFilter: "authors",
            entityToSelect: "countries",
            displayName: "institution country",
            type: "selectEntity",
            isCountry: true,
            category: "geo",
            actions: ["filter", "group_by"],
            actionsPopular: ["filter", "group_by"],
            icon: "mdi-earth",
            extractFn: (entity) => {
                if (!entity.last_known_institutions) return null;
                return entity.last_known_institutions.map(insti => {
                    const result = countryCodeLookup.byIso(insti.country_code);
                    return result?.country || insti.country_code;
                });
            },
            column: { export: { path: "last_known_institutions.country_code" } },
        },
        {
            key: "last_known_institutions.type",
            entityToFilter: "authors",
            entityToSelect: "institution-types",
            displayName: "Institution type",
            type: "selectEntity",
            category: "institution",
            actions: ["filter", "group_by"],
            actionsPopular: ["filter",],
            icon: "mdi-shape-outline",
        },
        {
            key: "has_orcid",
            entityToFilter: "authors",
            entityToSelect: "authors",
            displayName: "Has an ORCID",
            type: "boolean",
            booleanValues: ["No ORCID", "Has ORCID"],
            actions: ["filter", "group_by"],
            actionsPopular: ["filter", "group_by"],
            category: "ids",
            icon: "mdi-tag-outline",
        },
        {
            key: "display_name_alternatives",
            entityToFilter: "authors",
            entityToSelect: "authors",
            displayName: "observed names",
            type: "selectEntity",
            category: "other",
            actions: [],
            actionsPopular: [],
            icon: "mdi-town-hall",
            isMultiple: true,
            extractFn: (entity) => altNames(entity, "display_name_alternatives"),
            // The extracted items are plain name strings, not author objects —
            // a ":ids" sibling makes no sense here (would render an empty cell).
            noIdsSibling: true,
        },

        // authors: summary_stats
        {
            key: "summary_stats.h_index",
            entityToFilter: "authors",
            entityToSelect: "authors",
            displayName: "h-index",
            type: "range",
            category: "citation",
            actions: ["filter"],
            actionsPopular: ["filter"],
            icon: "mdi-chart-line",
            isMultiple: true,
            extractFn: (entity) => entity.summary_stats.h_index,
        },
        {
            key: "summary_stats.i10_index",
            entityToFilter: "authors",
            entityToSelect: "authors",
            displayName: "i10-index",
            type: "range",
            category: "citation",
            actions: ["filter"],
            actionsPopular: ["filter"],
            icon: "mdi-chart-line",
            isMultiple: true,
            extractFn: (entity) => entity.summary_stats.i10_index,
        },
        {
            key: "summary_stats.2yr_mean_citedness",
            entityToFilter: "authors",
            entityToSelect: "authors",
            displayName: "2-year mean citedness",
            type: "range",
            category: "citation",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-chart-line",
            extractFn: (entity) => entity.summary_stats?.['2yr_mean_citedness'],
        },

        // ============================================================
        // SOURCES
        // ============================================================
        {
            key: "ids.openalex",
            entityToFilter: "sources",
            entityToSelect: "sources",
            displayName: "Source",
            isId: true,
            type: "selectEntity",
            category: "ids",
            icon: "mdi-book-open-outline",
            actions: ["column"],
            // OWN_ID_COLUMN: the source's own OpenAlex ID (S…).
            extractFn: (entity) => entity.id,
            column: {
                label: "Source ID",
                render: { kind: "stringList", bareId: true },
                export: { recipe: "bare_openalex_id" },
            },
        },
        {
            key: "default.search",
            entityToFilter: "sources",
            entityToSelect: "sources",
            displayName: "name",
            type: "search",
            category: "other",
            actions: ["filter"],
            actionsPopular: ["filter"],
            icon: "mdi-magnify",
        },
        {
            key: "issn",
            entityToFilter: "sources",
            displayName: "ISSN",
            type: "search",
            isId: true,
            actions: ["filter", "column"],
            actionsPopular: [],
            category: "ids",
            icon: "mdi-book-open-outline",
            isMultiple: true,
            extractFn: (e) => e.ids?.issn,
            // type:"search" is a filter; column.render opts it in as a column.
            // Server flat-path is `issn` (the |-joined list).
            column: { render: { kind: "stringList" }, export: { path: "issn" } },
            noIdsSibling: true,
        },
        {
            key: "issn_l",
            entityToFilter: "sources",
            displayName: "ISSN-L",
            type: "search",
            isId: true,
            actions: ["column"],
            category: "ids",
            icon: "mdi-book-open-outline",
            isMultiple: false,
            extractFn: (e) => e.issn_l,
            column: { render: { kind: "text" } },
        },
        {
            key: "country_code",
            entityToFilter: "sources",
            entityToSelect: "countries",
            displayName: "Country",
            type: "selectEntity",
            isCountry: true,
            category: "geo",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-earth",
            extractFn: (entity) => {
                if (!entity.country_code) return null;
                const r = countryCodeLookup.byIso(entity.country_code);
                return r?.country || entity.country_code;
            },
            column: { export: { path: "country_code" } },
        },
        {
            key: "first_publication_year",
            entityToFilter: "sources",
            displayName: "first publication year",
            type: "range",
            category: "other",
            actions: ["column"],
            icon: "mdi-calendar",
            extractFn: (entity) => entity.first_publication_year,
            column: { render: { kind: "year" } },
        },
        {
            key: "homepage_url",
            entityToFilter: "sources",
            displayName: "homepage",
            type: "selectEntity",
            actions: [],
            category: "other",
            icon: "mdi-web",
            extractFn: (entity) => entity.homepage_url,
        },
        {
            key: "display_name.search",
            entityToFilter: "sources",
            displayName: "Title search",
            type: "search",
            category: "other",
            icon: "mdi-magnify",
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "sources",
            displayName: "name",
            type: "search",
            actions: ["sort", "column", "edit"],
            actionsPopular: ["sort", "column",],
            category: "other",
            icon: "mdi-account-outline",
            extractFn: (entity) => entity.display_name,
            isMultiple: false,
        },
        {
            key: "publisher",
            entityToFilter: "sources",
            entityToSelect: "publishers",
            displayName: "Publisher",
            type: "selectEntity",
            isManyOptions: true,
            category: "other",
            actions: ["edit"],
            actionsPopular: [],
            icon: "mdi-domain",
            extractFn: (e) => {
                if (e.type !== "journal") return
                return {
                    id: e.host_organization,
                    display_name: e.host_organization_name,
                }
            },
            // The "publisher" of a source is the API's `host_organization*` —
            // there's no `publisher.*` flat-path. Bare-ID variant reads
            // `host_organization` (the canonical URL; bare_openalex_id strips it).
            column: { export: { path: "host_organization_name", idsPath: "host_organization" } },
        },
        {
            key: "type",
            entityToFilter: "sources",
            entityToSelect: "source-types",
            displayName: "Source type",
            type: "selectEntity",
            category: "other",
            actions: ["filter", "edit"],
            actionsPopular: ["filter"],
            icon: "mdi-shape-outline",
            extractFn: (e) => e.type,
        },
        {
            key: "topics.id",
            entityToFilter: "sources",
            entityToSelect: "topics",
            displayName: "topic",
            type: "selectEntity",
            isManyOptions: true,
            category: "aboutness",
            actions: ["filter", "group_by"],
            actionsPopular: ["filter", "group_by"],
            icon: "mdi-tag-outline",
        },
        {
            key: "apc_usd",
            entityToFilter: "sources",
            displayName: "Article Processing Charge",
            type: "range",
            category: "other",
            actions: ["filter", "edit"],
            actionsPopular: ["filter"],
            icon: "mdi-cash",
            extractFn: (e) => {
                if (!e.apc_usd) return
                return "$" + e.apc_usd.toLocaleString()
            },
        },
        {
            key: "is_oa",
            entityToFilter: "sources",
            displayName: "Fully open access",
            type: "boolean",
            booleanValues: ["Not Open Access", "Open Access"],
            category: "open access",
            actions: ["filter", "edit"],
            actionsPopular: ["filter"],
            icon: "mdi-lock-open-outline",
            extractFn: (e) => e.is_oa,
        },
        {
            key: "is_in_doaj",
            entityToFilter: "sources",
            displayName: "In DOAJ",
            type: "boolean",
            booleanValues: ["Not in DOAJ", "In DOAJ"],
            category: "open access",
            actions: ["filter", "edit"],
            actionsPopular: ["filter"],
            icon: "mdi-lock-open-outline",
            extractFn: (entity) => entity.is_in_doaj,
        },
        {
            key: "is_core",
            entityToFilter: "sources",
            displayName: "CWTS Core source",
            type: "boolean",
            booleanValues: ["Not a CWTS Core source", "CWTS Core source"],
            category: "other",
            actions: ["filter", "group_by"],
            actionsPopular: ["filter"],
            icon: "mdi-book-open-outline",
            extractFn: (entity) => entity.is_core,
        },
        {
            key: "alternate_titles",
            entityToFilter: "sources",
            displayName: "alternate names",
            type: "selectEntity",
            category: "other",
            actions: ["edit"],
            actionsPopular: [],
            icon: "mdi-book-open-outline",
            isMultiple: true,
            extractFn: (entity) => altNames(entity, "alternate_titles"),
        },

        // sources: summary_stats
        {
            key: "summary_stats.2yr_mean_citedness",
            entityToFilter: "sources",
            displayName: "2yr mean citedness",
            type: "range",
            category: "citation",
            actions: ["filter", "sort"],
            actionsPopular: ["filter", "sort"],
            icon: "mdi-chart-line",
            isMultiple: true,
            extractFn: (entity) => entity.summary_stats["2yr_mean_citedness"],
        },
        {
            key: "summary_stats.h_index",
            entityToFilter: "sources",
            displayName: "h-index",
            type: "range",
            category: "citation",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-chart-line",
            isMultiple: true,
            extractFn: (entity) => entity.summary_stats.h_index,
        },
        {
            key: "summary_stats.i10_index",
            entityToFilter: "sources",
            displayName: "i10-index",
            type: "range",
            category: "citation",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-chart-line",
            isMultiple: true,
            extractFn: (entity) => entity.summary_stats.i10_index,
        },

        // ============================================================
        // PUBLISHERS
        // ============================================================
        {
            key: "ids.openalex",
            entityToFilter: "publishers",
            entityToSelect: "publishers",
            displayName: "Publisher",
            isId: true,
            type: "selectEntity",
            category: "ids",
            icon: "mdi-domain",
            actions: ["column"],
            // OWN_ID_COLUMN: the publisher's own OpenAlex ID (P…).
            extractFn: (entity) => entity.id,
            column: {
                label: "Publisher ID",
                render: { kind: "stringList", bareId: true },
                export: { recipe: "bare_openalex_id" },
            },
        },
        {
            key: "display_name.search",
            entityToFilter: "publishers",
            displayName: "Name search",
            type: "search",
            category: "other",
            icon: "mdi-magnify",
        },

        // ============================================================
        // FUNDERS
        // ============================================================
        {
            key: "ids.openalex",
            entityToFilter: "funders",
            entityToSelect: "funders",
            displayName: "Funder",
            isId: true,
            type: "selectEntity",
            category: "ids",
            icon: "mdi-cash-multiple",
            actions: ["column"],
            // OWN_ID_COLUMN: the funder's own OpenAlex ID (F…).
            extractFn: (entity) => entity.id,
            column: {
                label: "Funder ID",
                render: { kind: "stringList", bareId: true },
                export: { recipe: "bare_openalex_id" },
            },
        },
        {
            key: "default.search",
            entityToFilter: "funders",
            entityToSelect: "funders",
            displayName: "name",
            type: "search",
            category: "other",
            actions: ["filter"],
            actionsPopular: ["filter"],
            icon: "mdi-magnify",
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "funders",
            displayName: "name",
            type: "search",
            actions: ["sort", "column",],
            actionsPopular: ["sort", "column",],
            category: "other",
            icon: "mdi-cash-multiple",
            extractFn: (entity) => entity.display_name,
            isMultiple: false,
        },
        {
            key: "ids.ror",
            entityToFilter: "funders",
            entityToSelect: "funders",
            displayName: "ROR",
            isId: true,
            type: "selectEntity",
            category: "ids",
            icon: "mdi-cash-multiple",
            extractFn: (e) => e.ids?.ror,
        },
        {
            key: "ids.wikidata",
            entityToFilter: "funders",
            displayName: "Wikidata ID",
            isId: true,
            type: "selectEntity",
            category: "ids",
            actions: [],
            actionsPopular: [],
            icon: "mdi-web",
            extractFn: (e) => e.ids?.wikidata,
        },
        {
            key: "ids.crossref",
            entityToFilter: "funders",
            displayName: "Crossref ID",
            isId: true,
            type: "selectEntity",
            category: "ids",
            actions: [],
            actionsPopular: [],
            icon: "mdi-identifier",
            extractFn: (e) => e.ids?.crossref,
        },
        {
            key: "ids.doi",
            entityToFilter: "funders",
            displayName: "DOI",
            isId: true,
            type: "selectEntity",
            category: "ids",
            actions: [],
            actionsPopular: [],
            icon: "mdi-identifier",
            extractFn: (e) => e.ids?.doi,
        },
        {
            key: "display_name.search",
            entityToFilter: "funders",
            displayName: "Name search",
            type: "search",
            category: "other",
            icon: "mdi-magnify",
        },
        {
            key: "country_code",
            entityToFilter: "funders",
            entityToSelect: "countries",
            displayName: "Country",
            type: "selectEntity",
            isManyOptions: true,
            isCountry: true,
            category: "geo",
            actions: ["filter"],
            actionsPopular: ["filter"],
            icon: "mdi-earth",
            extractFn: (entity) => entity.country_code,
        },
        {
            key: "is_global_south",
            entityToFilter: "funders",
            displayName: "Global South",
            type: "boolean",
            actions: ["filter", "group_by"],
            actionsPopular: ["group_by"],
            category: "geo",
            booleanValues: ["Global North", "Global South"],
            icon: "mdi-earth",
            extractFn: (entity) => entity.is_global_south,
        },
        {
            key: "alternate_titles",
            entityToFilter: "funders",
            displayName: "alternate names",
            type: "selectEntity",
            category: "other",
            actions: [],
            actionsPopular: [],
            icon: "mdi-cash-multiple",
            isMultiple: true,
            extractFn: (entity) => altNames(entity, "alternate_titles"),
        },
        {
            key: "description",
            entityToFilter: "funders",
            displayName: "description",
            type: "selectEntity",
            category: "other",
            actions: [],
            actionsPopular: [],
            icon: "mdi-text",
            isMultiple: false,
            extractFn: (entity) => entity.description,
        },
        {
            key: "homepage_url",
            entityToFilter: "funders",
            displayName: "homepage URL",
            type: "selectEntity",
            category: "other",
            actions: [],
            actionsPopular: [],
            icon: "mdi-web",
            isMultiple: false,
            extractFn: (entity) => entity.homepage_url,
        },
        {
            key: "awards_count",
            entityToFilter: "funders",
            displayName: "awards count",
            type: "range",
            category: "citation",
            actions: ["sort", "column"],
            actionsPopular: ["sort", "column"],
            icon: "mdi-cash-multiple",
            extractFn: (entity) => entity.awards_count,
        },
        {
            key: "works_count",
            entityToFilter: "funders",
            displayName: "works count",
            type: "range",
            category: "citation",
            actions: ["sort", "column"],
            actionsPopular: ["sort", "column"],
            icon: "mdi-file-document-outline",
            extractFn: (entity) => entity.works_count,
        },
        {
            key: "cited_by_count",
            entityToFilter: "funders",
            displayName: "cited by count",
            type: "range",
            category: "citation",
            actions: ["sort", "column"],
            actionsPopular: ["sort", "column"],
            icon: "mdi-format-quote-close",
            extractFn: (entity) => entity.cited_by_count,
        },
        {
            key: "summary_stats.2yr_mean_citedness",
            entityToFilter: "funders",
            displayName: "2-year mean citedness",
            type: "range",
            category: "citation",
            actions: ["sort", "column"],
            actionsPopular: [],
            icon: "mdi-chart-line",
            extractFn: (entity) => entity.summary_stats?.['2yr_mean_citedness'],
        },
        {
            key: "summary_stats.h_index",
            entityToFilter: "funders",
            displayName: "h-index",
            type: "range",
            category: "citation",
            actions: ["sort", "column"],
            actionsPopular: [],
            icon: "mdi-chart-bar",
            extractFn: (entity) => entity.summary_stats?.h_index,
        },
        {
            key: "summary_stats.i10_index",
            entityToFilter: "funders",
            displayName: "i10-index",
            type: "range",
            category: "citation",
            actions: ["sort", "column"],
            actionsPopular: [],
            icon: "mdi-chart-bar",
            extractFn: (entity) => entity.summary_stats?.i10_index,
        },

        // ============================================================
        // INSTITUTIONS
        // ============================================================
        {
            key: "ids.openalex",
            entityToFilter: "institutions",
            entityToSelect: "institutions",
            displayName: "Institution",
            isId: true,
            type: "selectEntity",
            category: "ids",
            icon: "mdi-town-hall",
            actions: ["column"],
            // OWN_ID_COLUMN: the institution's own OpenAlex ID (I…).
            extractFn: (entity) => entity.id,
            column: {
                label: "Institution ID",
                render: { kind: "stringList", bareId: true },
                export: { recipe: "bare_openalex_id" },
            },
        },
        {
            key: "default.search",
            entityToFilter: "institutions",
            entityToSelect: "institutions",
            displayName: "name",
            type: "search",
            category: "other",
            actions: ["filter"],
            actionsPopular: ["filter"],
            icon: "mdi-magnify",
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "institutions",
            displayName: "name",
            type: "search",
            actions: ["sort", "column",],
            actionsPopular: ["sort", "column",],
            category: "other",
            icon: "mdi-account-outline",
            extractFn: (entity) => entity.display_name,
            isMultiple: false,
        },
        {
            key: "homepage_url",
            entityToFilter: "institutions",
            displayName: "homepage",
            type: "selectEntity",
            actions: [],
            category: "other",
            icon: "mdi-web",
            extractFn: (entity) => entity.homepage_url,
        },
        {
            key: "ids.ror",
            entityToFilter: "institutions",
            entityToSelect: "institutions",
            displayName: "ROR",
            isId: true,
            type: "selectEntity",
            category: "ids",
            icon: "mdi-town-hall",
            extractFn: (e) => e.ids.ror,
        },
        {
            key: "display_name.search",
            entityToFilter: "institutions",
            displayName: "Name search",
            type: "search",
            category: "other",
            icon: "mdi-magnify",
        },
        {
            key: "country_code",
            entityToFilter: "institutions",
            entityToSelect: "countries",
            displayName: "Country",
            type: "selectEntity",
            isManyOptions: true,
            isCountry: true,
            category: "geo",
            actions: ["filter"],
            actionsPopular: ["filter"],
            icon: "mdi-earth",
            // Column shows the country NAME (codebook lookup); CSV ships the raw
            // ISO code at the `country_code` flat-path (accepted CODE_VS_NAME).
            extractFn: (entity) => {
                if (!entity.country_code) return null;
                const r = countryCodeLookup.byIso(entity.country_code);
                return r?.country || entity.country_code;
            },
            column: { export: { path: "country_code" } },
        },
        {
            key: "type",
            entityToFilter: "institutions",
            entityToSelect: "institution-types",
            displayName: "Institution type",
            type: "selectEntity",
            category: "other",
            actions: ["filter", "edit"],
            actionsPopular: ["filter"],
            icon: "mdi-shape-outline",
            extractFn: (entity) => entity.type,
        },
        {
            key: "x_concepts.id",
            entityToFilter: "institutions",
            displayName: "Concepts",
            entityToSelect: "concepts",
            type: "selectEntity",
            isManyOptions: true,
            category: "other",
            icon: "mdi-tag-outline",
        },
        {
            key: "display_name_alternatives",
            entityToFilter: "institutions",
            displayName: "alternate names",
            type: "selectEntity",
            category: "other",
            actions: [],
            actionsPopular: [],
            icon: "mdi-town-hall",
            isMultiple: true,
            extractFn: (entity) => altNames(entity, "display_name_alternatives"),
            noIdsSibling: true,
        },
        {
            key: "parent_institutions",
            entityToFilter: "institutions",
            displayName: "parent institutions",
            type: "selectEntity",
            category: "other",
            actions: [],
            actionsPopular: [],
            icon: "mdi-town-hall",
            isMultiple: true,
            extractFn: (entity) => entity.associated_institutions.filter(i => {
                return i.relationship === "parent"
            }),
        },
        {
            key: "child_institutions",
            entityToFilter: "institutions",
            displayName: "child institutions",
            type: "selectEntity",
            category: "other",
            actions: [],
            actionsPopular: [],
            icon: "mdi-town-hall",
            isMultiple: true,
            extractFn: (entity) => (entity.associated_institutions ?? []).filter(i => {
                return i.relationship === "child"
            }),
            // Server flatten has no `child_institutions.*` path — the
            // relationship filter is client-only. Export ships the superset
            // (all associated institutions) so the CSV at least carries
            // data; the table shows the filtered subset on screen.
            // Accepted as FILTERED_SUBSET divergence in the parity sweep.
            column: { export: { path: "associated_institutions.display_name", idsPath: "associated_institutions.id" } },
        },
        {
            key: "related_institutions",
            entityToFilter: "institutions",
            displayName: "related institutions",
            type: "selectEntity",
            category: "other",
            actions: [],
            actionsPopular: [],
            icon: "mdi-town-hall",
            isMultiple: true,
            extractFn: (entity) => (entity.associated_institutions ?? []).filter(i => {
                return i.relationship === "related"
            }),
            // See `child_institutions` — same FILTERED_SUBSET pattern.
            column: { export: { path: "associated_institutions.display_name", idsPath: "associated_institutions.id" } },
        },
        {
            key: "lineage",
            entityToFilter: "institutions",
            entityToSelect: "institutions",
            displayName: "lineage",
            type: "selectEntity",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-town-hall",
            isMultiple: true,
        },
        // institutions: geo (column-only — no `actions`, so absent from the
        // filter dialog but offered in the column picker; geo.city/region are
        // not OX filters). flat-paths: geo.city, geo.region.
        {
            key: "geo.city",
            entityToFilter: "institutions",
            displayName: "city",
            type: "text",
            category: "geo",
            actions: [],
            actionsPopular: [],
            icon: "mdi-map-marker-outline",
            extractFn: (entity) => entity.geo?.city,
        },
        {
            key: "geo.region",
            entityToFilter: "institutions",
            displayName: "region",
            type: "text",
            category: "geo",
            actions: [],
            actionsPopular: [],
            icon: "mdi-map-marker-outline",
            extractFn: (entity) => entity.geo?.region,
        },
        {
            key: "display_name_acronyms",
            entityToFilter: "institutions",
            displayName: "acronyms",
            type: "selectEntity",
            category: "other",
            actions: ["column"],
            actionsPopular: [],
            icon: "mdi-town-hall",
            isMultiple: true,
            extractFn: (entity) => entity.display_name_acronyms,
            // Plain strings (UM, UMich…), not entity objects.
            noIdsSibling: true,
            column: { render: { kind: "stringList" } },
        },
        // institutions: summary_stats
        {
            key: "summary_stats.2yr_mean_citedness",
            entityToFilter: "institutions",
            displayName: "2-year mean citedness",
            type: "range",
            category: "citation",
            actions: ["sort", "column"],
            actionsPopular: [],
            icon: "mdi-chart-line",
            extractFn: (entity) => entity.summary_stats?.['2yr_mean_citedness'],
        },
        {
            key: "summary_stats.h_index",
            entityToFilter: "institutions",
            displayName: "h-index",
            type: "range",
            category: "citation",
            actions: ["sort", "column"],
            actionsPopular: [],
            icon: "mdi-chart-bar",
            extractFn: (entity) => entity.summary_stats?.h_index,
        },
        {
            key: "summary_stats.i10_index",
            entityToFilter: "institutions",
            displayName: "i10-index",
            type: "range",
            category: "citation",
            actions: ["sort", "column"],
            actionsPopular: [],
            icon: "mdi-chart-bar",
            extractFn: (entity) => entity.summary_stats?.i10_index,
        },

        // ============================================================
        // CONCEPTS
        // ============================================================
        {
            key: "ids.openalex",
            entityToFilter: "concepts",
            entityToSelect: "concepts",
            displayName: "Concept",
            isId: true,
            type: "selectEntity",
            category: "ids",
            icon: "mdi-tag-outline",
            actions: ["column"],
            // OWN_ID_COLUMN: the concept's own OpenAlex ID (C…).
            extractFn: (entity) => entity.id,
            column: {
                label: "Concept ID",
                render: { kind: "stringList", bareId: true },
                export: { recipe: "bare_openalex_id" },
            },
        },
        {
            key: "display_name.search",
            entityToFilter: "concepts",
            displayName: "Name search",
            type: "search",
            category: "other",
            icon: "mdi-magnify",
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "concepts",
            displayName: "name",
            type: "search",
            actions: ["sort", "column"],
            actionsPopular: ["sort", "column"],
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.display_name,
            isMultiple: false,
        },
        {
            key: "description",
            entityToFilter: "concepts",
            displayName: "description",
            type: "search",
            category: "other",
            actions: ["column"],
            icon: "mdi-text",
            extractFn: (entity) => entity.description,
            column: { render: { kind: "text" } },
        },
        {
            key: "level",
            entityToFilter: "concepts",
            displayName: "Level",
            maxPotentialFiltersToShow: 10,
            type: "selectEntity",
            category: "other",
            actions: ["column"],
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.level,
            column: { render: { kind: "number" } },
        },

        // ============================================================
        // TOPICS
        // ============================================================
        {
            key: "ids.openalex",
            entityToFilter: "topics",
            entityToSelect: "topics",
            displayName: "Topic",
            isId: true,
            type: "selectEntity",
            category: "ids",
            icon: "mdi-tag-outline",
            actions: ["column"],
            // OWN_ID_COLUMN: the topic's own OpenAlex ID (T…).
            extractFn: (entity) => entity.id,
            column: {
                label: "Topic ID",
                render: { kind: "stringList", bareId: true },
                export: { recipe: "bare_openalex_id" },
            },
        },
        {
            key: "description",
            entityToFilter: "topics",
            entityToSelect: "topics",
            displayName: "description",
            type: "search",
            category: "other",
            actions: ["column"],
            icon: "mdi-tag-outline",
            extractFn: (e) => e.description,
            // type:"search" stays (it's a filter); the column.render block makes
            // it ALSO a column (deriveColumnRender checks column.render first).
            column: { render: { kind: "text" } },
        },
        {
            key: "keywords",
            entityToFilter: "topics",
            displayName: "keywords",
            type: "text",
            category: "other",
            actions: ["column"],
            actionsPopular: [],
            icon: "mdi-tag-outline",
            isMultiple: true,
            extractFn: (entity) => entity.keywords,
            noIdsSibling: true,
            column: { render: { kind: "stringList" } },
        },
        {
            key: "siblings",
            entityToFilter: "topics",
            entityToSelect: "topics",
            displayName: "related topics (siblings)",
            type: "selectEntity",
            actions: [],  // #294: no server `siblings` filter; entity-page display only
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.siblings,
        },
        {
            key: "subfield.id",
            entityToFilter: "topics",
            entityToSelect: "topics",
            displayName: "subfield (parent)",
            type: "selectEntity",
            actions: ["filter", "group_by"],
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.subfield,
        },
        {
            key: "field.id",
            entityToFilter: "topics",
            entityToSelect: "topics",
            displayName: "field",
            type: "selectEntity",
            actions: ["filter", "group_by"],
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.field,
        },
        {
            key: "domain.id",
            entityToFilter: "topics",
            entityToSelect: "topics",
            displayName: "domain",
            type: "selectEntity",
            actions: ["filter", "group_by"],
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.domain,
        },

        // ============================================================
        // SUBFIELDS
        // ============================================================
        {
            key: "description",
            entityToFilter: "subfields",
            entityToSelect: "subfields",
            displayName: "description",
            type: "search",
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.description,
        },
        {
            key: "display_name_alternatives",
            entityToFilter: "subfields",
            entityToSelect: "subfields",
            displayName: "alternate names",
            type: "search",
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => altNames(e, "display_name_alternatives"),
        },
        {
            key: "topics",
            entityToFilter: "subfields",
            entityToSelect: "subfields",
            displayName: "topics (children)",
            type: "selectEntity",
            actions: [],  // #294: no server `topics` filter on /subfields; entity-page display only
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.topics,
        },
        {
            key: "siblings",
            entityToFilter: "subfields",
            entityToSelect: "subfields",
            displayName: "related subfields (siblings)",
            type: "selectEntity",
            actions: [],  // #294: no server `siblings` filter; entity-page display only
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.siblings,
        },
        {
            key: "field.id",
            entityToFilter: "subfields",
            entityToSelect: "subfields",
            displayName: "field (parent)",
            type: "selectEntity",
            actions: ["filter", "group_by"],
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.field,
        },
        {
            key: "domain.id",
            entityToFilter: "subfields",
            entityToSelect: "subfields",
            displayName: "domain",
            type: "selectEntity",
            actions: ["filter", "group_by"],
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.domain,
        },

        // ============================================================
        // FIELDS
        // ============================================================
        {
            key: "description",
            entityToFilter: "fields",
            entityToSelect: "fields",
            displayName: "description",
            type: "search",
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.description,
        },
        {
            key: "display_name_alternatives",
            entityToFilter: "fields",
            entityToSelect: "fields",
            displayName: "alternate names",
            type: "search",
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => altNames(e, "display_name_alternatives"),
        },
        {
            key: "siblings",
            entityToFilter: "fields",
            entityToSelect: "fields",
            displayName: "related fields (siblings)",
            type: "selectEntity",
            actions: [],  // #294: no server `siblings` filter; entity-page display only
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.siblings,
        },
        {
            key: "subfields",
            entityToFilter: "fields",
            entityToSelect: "fields",
            displayName: "subfields (children)",
            type: "selectEntity",
            actions: [],  // #294: no server `subfields` filter on /fields; entity-page display only
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.subfields,
        },
        {
            key: "domain.id",
            entityToFilter: "fields",
            entityToSelect: "fields",
            displayName: "domain (parent)",
            type: "selectEntity",
            actions: ["filter", "group_by"],
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.domain,
        },

        // ============================================================
        // DOMAINS
        // ============================================================
        {
            key: "description",
            entityToFilter: "domains",
            entityToSelect: "domains",
            displayName: "description",
            type: "search",
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.description,
        },
        {
            key: "display_name_alternatives",
            entityToFilter: "domains",
            entityToSelect: "domains",
            displayName: "alternate names",
            type: "search",
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => altNames(e, "display_name_alternatives"),
        },
        {
            key: "fields",
            entityToFilter: "domains",
            entityToSelect: "domains",
            displayName: "fields (children)",
            type: "selectEntity",
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.fields,
        },
        {
            key: "siblings",
            entityToFilter: "domains",
            entityToSelect: "domains",
            displayName: "other domains (siblings)",
            type: "selectEntity",
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.siblings,
        },

        // ============================================================
        // TYPES
        // ============================================================
        {
            key: "description",
            entityToFilter: "types",
            entityToSelect: "types",
            displayName: "description",
            type: "search",
            category: "other",
            icon: "mdi-shape-outline",
            extractFn: (e) => e.description,
        },
        {
            key: "crossref_types",
            entityToFilter: "types",
            entityToSelect: "types",
            displayName: "alternate names (Crossref)",
            type: "selectEntity",
            category: "other",
            icon: "mdi-shape-outline",
            extractFn: (e) => e.crossref_types,
        },

        // ============================================================
        // CONTINENTS
        // ============================================================
        {
            key: "countries",
            entityToFilter: "continents",
            entityToSelect: "continents",
            displayName: "countries",
            type: "selectEntity",
            category: "other",
            icon: "mdi-earth",
            extractFn: (e) => e.countries,
        },

        // ============================================================
        // LOCATIONS
        // ============================================================
        {
            key: "work_id",
            entityToFilter: "locations",
            entityToSelect: "works",
            displayName: "work",
            type: "selectEntity",
            category: "other",
            icon: "mdi-file-document-outline",
            extractFn: (e) => e.work,
        },
        {
            key: "landing_page_url",
            entityToFilter: "locations",
            displayName: "landing page URL",
            type: "search",
            category: "other",
            icon: "mdi-link",
            extractFn: (e) => e.landing_page_url,
        },
        {
            key: "pdf_url",
            entityToFilter: "locations",
            displayName: "PDF URL",
            type: "search",
            category: "other",
            icon: "mdi-file-pdf-box",
            extractFn: (e) => e.pdf_url,
        },
        {
            key: "native_id",
            entityToFilter: "locations",
            displayName: "native ID",
            type: "search",
            category: "other",
            icon: "mdi-identifier",
            extractFn: (e) => e.native_id,
        },
        {
            key: "native_id_namespace",
            entityToFilter: "locations",
            displayName: "native ID namespace",
            type: "search",
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.native_id_namespace,
        },
        {
            key: "id",
            entityToFilter: "locations",
            displayName: "location ID",
            type: "search",
            category: "other",
            icon: "mdi-identifier",
            extractFn: (e) => e.id,
        },
        {
            key: "provenance",
            entityToFilter: "locations",
            displayName: "provenance",
            type: "search",
            category: "other",
            icon: "mdi-source-branch",
            extractFn: (e) => e.provenance,
        },
        {
            key: "title",
            entityToFilter: "locations",
            displayName: "title",
            type: "search",
            category: "other",
            icon: "mdi-text",
            extractFn: (e) => e.title,
        },
        {
            key: "type",
            entityToFilter: "locations",
            displayName: "type",
            type: "search",
            category: "other",
            icon: "mdi-shape-outline",
            extractFn: (e) => e.type,
        },
        {
            key: "source_name",
            entityToFilter: "locations",
            displayName: "source name",
            type: "search",
            category: "other",
            icon: "mdi-book-open-outline",
            extractFn: (e) => e.source_name,
        },
        {
            key: "publisher",
            entityToFilter: "locations",
            displayName: "publisher",
            type: "search",
            category: "other",
            icon: "mdi-domain",
            extractFn: (e) => e.publisher,
        },
        {
            key: "source_id",
            entityToFilter: "locations",
            entityToSelect: "sources",
            displayName: "source",
            type: "selectEntity",
            category: "other",
            icon: "mdi-book-open-outline",
            extractFn: (e) => e.source,
        },
        {
            key: "is_oa",
            entityToFilter: "locations",
            displayName: "is open access",
            type: "boolean",
            category: "other",
            icon: "mdi-lock-open-outline",
            extractFn: (e) => e.is_oa,
        },
        {
            key: "version",
            entityToFilter: "locations",
            displayName: "version",
            type: "search",
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (e) => e.version,
        },
        {
            key: "license",
            entityToFilter: "locations",
            entityToSelect: "licenses",
            displayName: "license",
            type: "search",
            category: "other",
            icon: "mdi-lock-open-outline",
            extractFn: (e) => e.license,
        },
        {
            key: "language",
            entityToFilter: "locations",
            entityToSelect: "languages",
            displayName: "language",
            type: "search",
            category: "other",
            icon: "mdi-translate",
            extractFn: (e) => e.language,
        },

        // ============================================================
        // AWARDS
        // ============================================================
        {
            key: "primary_topic.id",
            entityToFilter: "awards",
            displayName: "topic",
            entityToSelect: "topics",
            type: "selectEntity",
            isManyOptions: true,
            category: "aboutness",
            actions: ["filter", "group_by"],
            actionsPopular: ["filter", "group_by"],
            icon: "mdi-tag-outline",
            isMultiple: false,
            extractFn: (entity) => entity.primary_topic,
        },
        {
            key: "primary_topic.subfield.id",
            entityToFilter: "awards",
            displayName: "subfield",
            entityToSelect: "subfields",
            type: "selectEntity",
            isManyOptions: true,
            category: "aboutness",
            actions: ["filter", "group_by"],
            actionsPopular: [],
            icon: "mdi-tag-outline",
            isMultiple: false,
            extractFn: (entity) => entity.primary_topic?.subfield,
        },
        {
            key: "primary_topic.field.id",
            entityToFilter: "awards",
            displayName: "field",
            entityToSelect: "fields",
            type: "selectEntity",
            isManyOptions: true,
            category: "aboutness",
            actions: ["filter", "group_by"],
            actionsPopular: [],
            icon: "mdi-tag-outline",
            isMultiple: false,
            extractFn: (entity) => entity.primary_topic?.field,
        },
        {
            key: "primary_topic.domain.id",
            entityToFilter: "awards",
            displayName: "domain",
            entityToSelect: "domains",
            type: "selectEntity",
            isManyOptions: true,
            category: "aboutness",
            actions: ["filter", "group_by"],
            actionsPopular: [],
            icon: "mdi-tag-outline",
            isMultiple: false,
            extractFn: (entity) => entity.primary_topic?.domain,
        },
        {
            key: "institution_awarded.lineage",
            entityToFilter: "awards",
            displayName: "awarded institution",
            entityToSelect: "institutions",
            type: "selectEntity",
            isManyOptions: true,
            category: "institution",
            actions: ["filter", "group_by"],
            actionsPopular: ["filter", "group_by"],
            icon: "mdi-town-hall",
            isMultiple: false,
            extractFn: (entity) => entity.institution_awarded,
        },
        {
            key: "institution_awarded.country_code",
            entityToFilter: "awards",
            displayName: "awarded country",
            entityToSelect: "countries",
            type: "selectEntity",
            isCountry: true,
            category: "geo",
            actions: ["filter", "group_by"],
            actionsPopular: ["filter", "group_by"],
            icon: "mdi-earth",
            isMultiple: false,
            extractFn: (entity) => entity.institution_awarded?.country_code,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "awards",
            displayName: "title",
            type: "search",
            actions: [ "column"],
            actionsPopular: [ "column"],
            category: "other",
            icon: "mdi-file-document-outline",
            extractFn: (entity) => entity.display_name,
            isMultiple: false,
        },
        {
            key: "amount",
            entityToFilter: "awards",
            displayName: "amount",
            type: "range",
            actions: ["sort", "column", "filter"],
            actionsPopular: ["sort", "column"],
            category: "other",
            icon: "mdi-cash",
            sortByValue: true,
            isMultiple: false,
            extractFn: (entity) => entity.amount,
        },
        {
            key: "funder.id",
            entityToFilter: "awards",
            entityToSelect: "funders",
            displayName: "funder",
            type: "selectEntity",
            isManyOptions: true,
            category: "funder",
            actions: ["filter", "column", "group_by"],
            actionsPopular: ["filter", "group_by"],
            icon: "mdi-cash-multiple",
            extractFn: (entity) => entity.funder,
            isMultiple: false,
        },
        {
            key: "funding_type",
            entityToFilter: "awards",
            displayName: "funding type",
            type: "selectEntity",
            category: "other",
            actions: ["filter", "column", "group_by"],
            actionsPopular: ["filter", "group_by"],
            icon: "mdi-tag-outline",
            isMultiple: false,
            extractFn: (entity) => entity.funding_type,
        },
        {
            // #294: server has no `start_date` filter (only `start_year`); was a broken
            // filter chip. Keep as display column; year filtering moved to `start_year`.
            key: "start_date",
            entityToFilter: "awards",
            displayName: "start date",
            type: "range",
            category: "other",
            actions: ["column"],
            actionsPopular: [],
            icon: "mdi-calendar-start",
            isMultiple: false,
            extractFn: (entity) => entity.start_date,
        },
        {
            // #294: server has no `end_date` filter (only `end_year`); was a broken
            // filter chip. Keep as display column; year filtering moved to `end_year`.
            key: "end_date",
            entityToFilter: "awards",
            displayName: "end date",
            type: "range",
            category: "other",
            actions: ["column"],
            actionsPopular: [],
            icon: "mdi-calendar-end",
            isMultiple: false,
            extractFn: (entity) => entity.end_date,
        },
        {
            // #294: server-supported year-range filter (RangeField start_year).
            key: "start_year",
            entityToFilter: "awards",
            displayName: "start year",
            type: "range",
            category: "other",
            actions: ["filter", "sort", "column"],
            actionsPopular: ["filter"],
            icon: "mdi-calendar-start",
            isMultiple: false,
            extractFn: (entity) => entity.start_year,
        },
        {
            // #294: server-supported year-range filter (RangeField end_year).
            key: "end_year",
            entityToFilter: "awards",
            displayName: "end year",
            type: "range",
            category: "other",
            actions: ["filter", "sort", "column"],
            actionsPopular: ["filter"],
            icon: "mdi-calendar-end",
            isMultiple: false,
            extractFn: (entity) => entity.end_year,
        },
        {
            key: "funded_outputs_count",
            entityToFilter: "awards",
            displayName: "funded outputs count",
            type: "range",
            category: "other",
            actions: ["filter", "sort", "column"],
            actionsPopular: ["sort", "column"],
            icon: "mdi-file-document-multiple-outline",
            sortByValue: true,
            isMultiple: false,
            extractFn: (entity) => entity.funded_outputs_count,
        },
        {
            key: "start_year",
            entityToFilter: "awards",
            displayName: "start year",
            isDate: true,
            type: "range",
            sortByValue: true,
            category: "other",
            actions: ["filter", "sort", "column", "group_by"],
            actionsPopular: ["filter", "sort", "column", "group_by"],
            icon: "mdi-calendar-range",
            extractFn: (entity) => entity.start_year,
            isMultiple: false,
        },
        {
            key: "end_year",
            entityToFilter: "awards",
            displayName: "end year",
            isDate: true,
            type: "range",
            sortByValue: true,
            category: "other",
            actions: ["filter", "sort", "column"],
            actionsPopular: [],
            icon: "mdi-calendar-range",
            extractFn: (entity) => entity.end_year,
            isMultiple: false,
        },
        {
            key: "currency",
            entityToFilter: "awards",
            displayName: "currency",
            type: "selectEntity",
            category: "other",
            actions: [],
            actionsPopular: [],
            icon: "mdi-currency-usd",
            isMultiple: false,
        },
        {
            key: "doi",
            entityToFilter: "awards",
            displayName: "DOI",
            type: "search",
            category: "ids",
            actions: ["filter", "column"],
            actionsPopular: [],
            icon: "mdi-identifier",
            extractFn: (entity) => entity.doi,
            isMultiple: false,
        },
        {
            key: "id",
            entityToFilter: "awards",
            displayName: "OpenAlex ID",
            type: "search",
            category: "ids",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-identifier",
            isMultiple: false,
        },
        {
            key: "funder_award_id",
            entityToFilter: "awards",
            displayName: "funder award ID",
            type: "search",
            category: "ids",
            actions: ["filter", "column"],
            actionsPopular: [],
            icon: "mdi-identifier",
            extractFn: (entity) => entity.funder_award_id,
            isMultiple: false,
        },
        {
            key: "funder.doi",
            entityToFilter: "awards",
            displayName: "funder DOI",
            type: "search",
            category: "funder",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-identifier",
            isMultiple: false,
        },
        {
            key: "funder.ror",
            entityToFilter: "awards",
            displayName: "funder ROR",
            type: "search",
            category: "funder",
            actions: ["filter"],
            actionsPopular: [],
            icon: "mdi-identifier",
            isMultiple: false,
        },
        
        
        {
            key: "lead_investigator.affiliation.country",
            entityToFilter: "awards",
            entityToSelect: "countries",
            displayName: "investigator country",
            type: "selectEntity",
            isManyOptions: true,
            isCountry: true,
            category: "investigator",
            actions: [],
            actionsPopular: [],
            icon: "mdi-earth",
            isMultiple: false,
        },
        {
            key: "lead_investigator.affiliation.name",
            entityToFilter: "awards",
            displayName: "investigator affiliation",
            type: "search",
            isManyOptions: true,
            category: "investigator",
            actions: [],
            actionsPopular: [],
            icon: "mdi-domain",
            isMultiple: false,
            extractFn: (entity) => entity.lead_investigator?.affiliation?.name,
        },
        {
            key: "investigators",
            entityToFilter: "awards",
            displayName: "investigator",
            type: "selectEntity",
            isManyOptions: true,
            category: "investigator",
            actions: ["column"],
            actionsPopular: [],
            icon: "mdi-account-outline",
            extractFn: (entity) => {
                // Combine lead_investigator, co_lead_investigator, and investigators array
                // Return plain strings (no links) since investigators aren't disambiguated
                const investigators = [];
                const addInvestigator = (inv) => {
                    const name = [inv.given_name, inv.family_name].filter(Boolean).join(' ');
                    if (name) investigators.push(name);
                };
                if (entity.lead_investigator) addInvestigator(entity.lead_investigator);
                if (entity.co_lead_investigator) addInvestigator(entity.co_lead_investigator);
                if (entity.investigators && Array.isArray(entity.investigators)) {
                    entity.investigators.forEach(addInvestigator);
                }
                return investigators;
            },
            isMultiple: true,
        },
        {
            key: "investigators.affiliation",
            entityToFilter: "awards",
            displayName: "institution",
            type: "selectEntity",
            isManyOptions: true,
            category: "investigator",
            actions: ["column"],
            actionsPopular: [],
            icon: "mdi-town-hall",
            extractFn: (entity) => {
                // Collect all unique affiliations from all investigators
                // Return plain strings since affiliations aren't linked to institution entities
                const seen = new Set();
                const addAffiliation = (inv) => {
                    if (inv?.affiliation?.name && !seen.has(inv.affiliation.name)) {
                        seen.add(inv.affiliation.name);
                    }
                };
                if (entity.lead_investigator) addAffiliation(entity.lead_investigator);
                if (entity.co_lead_investigator) addAffiliation(entity.co_lead_investigator);
                if (entity.investigators && Array.isArray(entity.investigators)) {
                    entity.investigators.forEach(addAffiliation);
                }
                return Array.from(seen);
            },
            isMultiple: true,
        },
        {
            key: "provenance",
            entityToFilter: "awards",
            displayName: "provenance",
            type: "selectEntity",
            category: "other",
            actions: ["filter", "group_by"],
            actionsPopular: [],
            icon: "mdi-source-branch",
            isMultiple: false,
            extractFn: (entity) => entity.provenance,
        },
        {
            key: "description",
            entityToFilter: "awards",
            displayName: "description",
            type: "text",
            category: "other",
            actions: ["column"],
            actionsPopular: [],
            icon: "mdi-text",
            extractFn: (entity) => entity.description,
            isMultiple: false,
        },
        {
            key: "landing_page_url",
            entityToFilter: "awards",
            displayName: "landing page",
            type: "url",
            category: "ids",
            actions: ["column"],
            actionsPopular: [],
            icon: "mdi-link",
            extractFn: (entity) => entity.landing_page_url,
            isMultiple: false,
        },
        {
            key: "funder_scheme",
            entityToFilter: "awards",
            displayName: "funder scheme",
            type: "selectEntity",
            isManyOptions: true,
            category: "funder",
            actions: ["filter", "column", "group_by"],
            actionsPopular: [],
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.funder_scheme,
            isMultiple: false,
        },

        // ============================================================
        // PUBLISHERS (entity page rows)
        // ============================================================
        {
            key: "alternate_titles",
            entityToFilter: "publishers",
            displayName: "alternate names",
            type: "selectEntity",
            actions: [],
            category: "other",
            icon: "mdi-domain",
            isMultiple: true,
            extractFn: (entity) => altNames(entity, "alternate_titles"),
        },
        {
            key: "parent_publisher",
            entityToFilter: "publishers",
            displayName: "parent publisher",
            type: "selectEntity",
            actions: [],
            category: "other",
            icon: "mdi-domain",
            extractFn: (entity) => entity.parent_publisher,
        },
        {
            key: "country_codes",
            entityToFilter: "publishers",
            displayName: "countries",
            type: "selectEntity",
            actions: [],
            category: "other",
            icon: "mdi-earth",
            isMultiple: true,
            extractFn: (entity) => {
                if (!entity.country_codes) return null;
                return entity.country_codes.map(code => {
                    const result = countryCodeLookup.byIso(code);
                    return result?.country || code;
                });
            },
        },
        {
            key: "homepage_url",
            entityToFilter: "publishers",
            displayName: "homepage",
            type: "selectEntity",
            actions: [],
            category: "other",
            icon: "mdi-web",
            extractFn: (entity) => entity.homepage_url,
        },
        {
            key: "ids.ror",
            entityToFilter: "publishers",
            displayName: "ROR",
            isId: true,
            type: "selectEntity",
            category: "ids",
            actions: [],
            icon: "mdi-domain",
            extractFn: (e) => e.ids?.ror,
        },
        {
            key: "ids.wikidata",
            entityToFilter: "publishers",
            displayName: "Wikidata ID",
            isId: true,
            type: "selectEntity",
            category: "ids",
            actions: [],
            icon: "mdi-web",
            extractFn: (e) => e.ids?.wikidata,
        },
        {
            key: "hierarchy_level",
            entityToFilter: "publishers",
            displayName: "hierarchy level",
            type: "range",
            category: "other",
            actions: [],
            icon: "mdi-file-tree",
            extractFn: (entity) => entity.hierarchy_level,
        },
        {
            key: "summary_stats.2yr_mean_citedness",
            entityToFilter: "publishers",
            displayName: "2-year mean citedness",
            type: "range",
            category: "citation",
            actions: [],
            icon: "mdi-chart-line",
            extractFn: (entity) => entity.summary_stats?.['2yr_mean_citedness'],
        },
        {
            key: "summary_stats.h_index",
            entityToFilter: "publishers",
            displayName: "h-index",
            type: "range",
            category: "citation",
            actions: [],
            icon: "mdi-chart-bar",
            extractFn: (entity) => entity.summary_stats?.h_index,
        },
        {
            key: "summary_stats.i10_index",
            entityToFilter: "publishers",
            displayName: "i10-index",
            type: "range",
            category: "citation",
            actions: [],
            icon: "mdi-chart-bar",
            extractFn: (entity) => entity.summary_stats?.i10_index,
        },

        // ============================================================
        // COUNTRIES (entity page rows)
        // ============================================================
        {
            key: "description",
            entityToFilter: "countries",
            displayName: "description",
            type: "search",
            category: "other",
            icon: "mdi-earth",
            extractFn: (e) => e.description,
        },
        {
            key: "display_name_alternatives",
            entityToFilter: "countries",
            displayName: "alternate names",
            type: "search",
            category: "other",
            icon: "mdi-earth",
            extractFn: (e) => altNames(e, "display_name_alternatives"),
        },
        {
            key: "continent",
            entityToFilter: "countries",
            displayName: "continent",
            type: "selectEntity",
            actions: [],
            category: "other",
            icon: "mdi-earth",
            extractFn: (e) => e.continent,
        },
        {
            key: "is_global_south",
            entityToFilter: "countries",
            displayName: "Global South",
            type: "boolean",
            actions: [],
            category: "other",
            icon: "mdi-earth",
            extractFn: (e) => e.is_global_south,
        },

        // ============================================================
        // SDGs (entity page rows)
        // ============================================================
        {
            key: "description",
            entityToFilter: "sdgs",
            displayName: "description",
            type: "search",
            category: "other",
            icon: "mdi-sprout-outline",
            extractFn: (e) => e.description,
        },

        // ============================================================
        // SIMPLE DESCRIPTION CONFIGS (source-types, institution-types, licenses, oa-statuses)
        // ============================================================
        {
            key: "description",
            entityToFilter: "source-types",
            displayName: "description",
            type: "search",
            category: "other",
            icon: "mdi-shape-outline",
            extractFn: (e) => e.description,
        },
        {
            key: "description",
            entityToFilter: "institution-types",
            displayName: "description",
            type: "search",
            category: "other",
            icon: "mdi-shape-outline",
            extractFn: (e) => e.description,
        },
        {
            key: "description",
            entityToFilter: "licenses",
            displayName: "description",
            type: "search",
            category: "other",
            icon: "mdi-lock-open-outline",
            extractFn: (e) => e.description,
        },
        {
            key: "description",
            entityToFilter: "oa-statuses",
            displayName: "description",
            type: "search",
            category: "other",
            icon: "mdi-lock-open-outline",
            extractFn: (e) => e.description,
        },

        // ============================================================
        // DISPLAY NAME CONFIGS FOR SECONDARY ENTITY TYPES
        // (enables column view in SERP table)
        // ============================================================
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "topics",
            displayName: "name",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "subfields",
            displayName: "name",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "fields",
            displayName: "name",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "domains",
            displayName: "name",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "publishers",
            displayName: "name",
            type: "search",
            actions: ["sort", "column"],
            actionsPopular: ["sort", "column"],
            category: "other",
            icon: "mdi-domain",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "types",
            displayName: "name",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-shape-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "continents",
            displayName: "name",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-earth",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "countries",
            displayName: "name",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-earth",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "languages",
            displayName: "name",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-translate",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "sdgs",
            displayName: "name",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-sprout-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "source-types",
            displayName: "name",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-shape-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "institution-types",
            displayName: "name",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-shape-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "licenses",
            displayName: "name",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-lock-open-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "oa-statuses",
            displayName: "name",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-lock-open-outline",
            extractFn: (entity) => entity.display_name,
        },
        {
            key: "display_name",
            isIdentityColumn: true,
            entityToFilter: "keywords",
            displayName: "name",
            type: "search",
            actions: ["column"],
            category: "other",
            icon: "mdi-tag-outline",
            extractFn: (entity) => entity.display_name,
        },
    ]

    const noFilterEntities = new Set([
        'domains', 'types', 'continents', 'sdgs',
        'source-types', 'institution-types', 'licenses', 'oa-statuses',
    ])
    const worksCountFilters = getEntityConfigs()
        .map(c => c.name)
        // #294: exclude 'locations' too — /locations has no works_count/cited_by_count
        // field, so the injected filter+sort chips 400.
        .filter(name => name !== 'works' && name !== 'awards' && name !== 'locations')
        .map(name => {
            const hasFilter = !noFilterEntities.has(name)
            return {
                key: "works_count",
                entityToFilter: name,
                displayName: "works count",
                type: "range",
                sortByValue: true,
                category: "citation",
                actions: hasFilter ? ["filter", "sort", "column"] : ["sort", "column"],
                actionsPopular: ["sort", "column",],
                icon: "mdi-file-document-multiple-outline",
                isMultiple: true,
                isDisplayedAsCount: true,
                extractFn: (entity) => entity.works_count,
            }
        })

    const citedByCountFilters = getEntityConfigs()
        .map(c => c.name)
        // #294: exclude 'locations' too — /locations has no works_count/cited_by_count
        // field, so the injected filter+sort chips 400.
        .filter(name => name !== 'works' && name !== 'awards' && name !== 'locations')
        .map(name => {
            const hasFilter = !noFilterEntities.has(name)
            return {
                key: "cited_by_count",
                entityToFilter: name,
                displayName: "citations count",
                type: "range",
                sortByValue: true,
                category: "citation",
                actions: hasFilter ? ["filter", "column", "sort"] : ["column", "sort"],
                actionsPopular: ["column", "sort"],
                icon: "mdi-format-quote-close",
                isMultiple: true,
                isDisplayedAsCount: true,
                extractFn: (entity) => entity.cited_by_count,
            }
        })

    // Inject a `collection` facet for every entity type that supports collections (v1).
    // The chip itself resolves values via /collections/<id> (see NoviceFilterChip)
    // and renders a picker that lists the current user's collections for the type.
    // facetConfigUtils.getFacetConfig also short-circuits on key === "collection"
    // for the same shape — both surfaces use this config.
    const COLLECTION_FACET_ENTITY_TYPES = [
        "works", "authors", "sources", "institutions",
        "topics", "sdgs", "funders", "publishers", "keywords", "concepts",
    ]
    const collectionFilters = COLLECTION_FACET_ENTITY_TYPES.map(name => ({
        key: "collection",
        entityToFilter: name,
        entityToSelect: "collections",
        // Human label only — the API filter key stays `collection:` (see #266/#228).
        // Per-entity "<Entity> is in collection" (oxjob #367): every collection filter
        // reads as membership of its type, and the standalone filter stops owning the
        // bare word "collection" (the #350 Zotero-overload trap). Sentence-cased, so it
        // is flagged `displayNameVerbatim` to bypass titleCase()/text-capitalize at the
        // chip + add-filter surfaces (which would wrongly yield "Work Is In Collection").
        displayName: collectionFilterLabel(name),
        displayNameVerbatim: true,
        type: "selectEntity",
        category: "other",
        actions: ["filter"],
        actionsPopular: [],
        icon: "mdi-folder-outline",
        isMultiple: true,
    }))

    const allConfigs = [
        ...ret,
        ...worksCountFilters,
        ...citedByCountFilters,
        ...collectionFilters,
    ]

    const manipulated = allConfigs
        // .filter(f => onlyReturnTheseFacets.includes(f.key))
        .map(config => {
            return {
                ...config,
                // values: [],
            }
        })
        .filter(config => {
            return !entityType || config.entityToFilter === entityType
        })

    manipulated.sort((a, b) => {
        return a.displayName.toLowerCase() > b.displayName.toLowerCase() ? 1 : -1
    })

    return manipulated
}


// Helper functions have been extracted to facetConfigUtils.js
// Import them from there: getFacetConfig, findFacetConfigs, facetsByCategory

export {
    facetConfigs,
    facetCategories,
    facetCategoriesIcons,
}
